/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/*
 * Product form variant id (Apple Pay / express checkout fix).
 *
 * product-blocks.liquid renders one name="id" field inside each product form. Add to Cart and the express checkout
 * buttons both send that field, so it must always hold the size the shopper picked, however it was picked:
 * - the theme's size buttons (the theme only moves the "selected" attribute, which stops working once any script
 *   has set the field's value, so the value is set again after every theme size change);
 * - VO Product Options or another app writing the field, writing its own "vopo-id" select (or a click in VO's #bcpo
 *   picking again the size that select already holds), or replacing the field;
 * - an app adding a second name="id" field (every name="id" field of the form is kept on the same value);
 * - an app's own size radios or checkboxes named "id": the checked one is read as the size, and the matching one is
 *   checked when the size changes elsewhere. Their values are never changed.
 * It checks on every tap, click, key press, change and submit, and again right before Add to Cart or an express
 * button handles the tap, so the id sent is always the current one.
 */
(function () {
  'use strict';

  if (typeof WeakMap !== 'function' || !Element.prototype.closest) return;

  var FORM_SELECTOR = 'form.ProductForm';
  var states = new WeakMap();
  var announced = new WeakMap();
  var timer = null;
  var voTapped = false;

  function sectionOf(form) {
    return form.closest('[data-section-type]') || document;
  }

  function stateOf(form) {
    var state = states.get(form);

    if (!state) {
      state = { agreed: null, seen: new WeakMap(), vopo: undefined, hint: null, ids: null, rendered: null, checked: undefined, checkedInit: false };
      states.set(form, state);
    }

    return state;
  }

  // Reads the section's product JSON once: the product's variant ids, and the id the page was rendered with.
  function loadProduct(form, state) {
    if (state.ids) return;
    state.ids = {};

    try {
      var data = JSON.parse(sectionOf(form).querySelector('[data-product-json]').textContent);
      data.product.variants.forEach(function (variant) {
        state.ids[String(variant.id)] = true;
      });
      if (data.selected_variant_id != null) state.rendered = String(data.selected_variant_id);
    } catch (error) {}
  }

  function isVariantOf(form, state, value) {
    if (typeof value !== 'string' || value === '') return false;
    loadProduct(form, state);

    return Object.keys(state.ids).length ? state.ids[value] === true : /^\d+$/.test(value);
  }

  function idFields(form) {
    var fields = [];

    for (var i = 0; i < form.elements.length; i++) {
      var element = form.elements[i];
      if (element.name === 'id' && !element.disabled && !isCheckable(element)) fields.push(element);
    }

    return fields;
  }

  function isCheckable(element) {
    return element.type === 'radio' || element.type === 'checkbox';
  }

  // An app's size radios (or checkboxes) named "id": their values are the sizes, so only which one is checked is read
  // or changed, never their values.
  function checkedId(form) {
    for (var i = 0; i < form.elements.length; i++) {
      var element = form.elements[i];
      if (element.name === 'id' && !element.disabled && isCheckable(element) && element.checked) return element.value;
    }

    return undefined;
  }

  function checkMatching(form, value) {
    for (var i = 0; i < form.elements.length; i++) {
      var element = form.elements[i];
      if (element.name === 'id' && !element.disabled && isCheckable(element)) element.checked = element.value === value;
    }
  }

  function vopoValue(form) {
    var section = sectionOf(form);
    var select = section.querySelector('select[name="vopo-id"]');

    if (!select && section.getAttribute && section.getAttribute('data-section-type') === 'product') {
      select = document.querySelector('select[name="vopo-id"]');
    }

    return select ? select.value : undefined;
  }

  // Keeps exactly one option carrying the "selected" attribute (theme.js needs one) and makes it the chosen one.
  function markSelected(select, option) {
    for (var i = 0; i < select.options.length; i++) {
      var current = select.options[i];
      if (current !== option && current.hasAttribute('selected')) current.removeAttribute('selected');
    }

    if (!option.hasAttribute('selected')) option.setAttribute('selected', 'selected');
  }

  function setField(field, value) {
    if (field.tagName === 'SELECT') {
      var option = null;

      for (var i = 0; i < field.options.length; i++) {
        if (field.options[i].value === value) option = field.options[i];
      }

      if (!option) return;
      markSelected(field, option);
    }

    field.value = value;
  }

  function syncForm(form, announce) {
    var state = stateOf(form);
    var fields = idFields(form);
    var vopo = vopoValue(form);
    var checked = checkedId(form);
    var previous = state.agreed;
    var pick = null;
    var i;

    if (!fields.length) return;

    // 1. The theme reported a size change (variant:changed).
    if (state.hint && isVariantOf(form, state, state.hint)) pick = state.hint;
    state.hint = null;

    // 2. An id field was written since the last check (by an app, or by the shopper in the no-JS dropdown).
    for (i = 0; !pick && i < fields.length; i++) {
      if (state.seen.has(fields[i]) && state.seen.get(fields[i]) !== fields[i].value && isVariantOf(form, state, fields[i].value)) {
        pick = fields[i].value;
      }
    }

    // 2b. An app's size radio (or checkbox) named "id" was checked since the last check.
    if (!pick && state.checkedInit && checked !== state.checked && isVariantOf(form, state, checked)) pick = checked;

    // 3. VO Product Options' own "vopo-id" select changed since the last check, or VO was clicked (picking again the
    //    size its select already held, after a theme size change). With checked radios named "id", step 2b sees that.
    if (!pick && state.vopo !== undefined && (vopo !== state.vopo || (voTapped && checked === undefined)) && isVariantOf(form, state, vopo)) pick = vopo;

    // 4. Nothing changed: a field added by an app takes the agreed id, and a lone replaced field is trusted.
    //    If no field holds a variant of this product, nothing is written (another app may have done it on purpose).
    for (i = 0; !pick && previous && i < fields.length; i++) {
      if (fields[i].value === previous) pick = previous;
    }
    if (!pick && isVariantOf(form, state, fields[0].value)) pick = fields[0].value;

    if (pick) {
      fields.forEach(function (field) {
        if (field.value !== pick) {
          setField(field, pick);
        } else if (field.tagName === 'SELECT' && field.selectedIndex > -1) {
          markSelected(field, field.options[field.selectedIndex]);
        }
      });
      state.agreed = pick;
      checkMatching(form, pick);
      checked = checkedId(form);
    }

    state.vopo = vopo;
    state.checked = checked;
    state.checkedInit = true;
    loadProduct(form, state);

    fields.forEach(function (field) {
      state.seen.set(field, field.value);

      // A field seen for the first time counts as already announced with the previous id or, for a form seen for the
      // first time (page load, or a quick view added later), with the id the page was rendered with, which is what
      // the express buttons read when they started.
      if (!announced.has(field)) announced.set(field, previous || state.rendered || field.value);

      // Tell listeners on the form (Shopify's express buttons, Shop Pay Installments, apps) that the size changed,
      // unless a bubbling change event for this value has already been sent from this field.
      if (announce && announced.get(field) !== field.value) {
        announced.set(field, field.value);
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function syncAll(announce) {
    var forms = document.querySelectorAll(FORM_SELECTOR);
    for (var i = 0; i < forms.length; i++) syncForm(forms[i], announce);
  }

  function syncLater() {
    if (timer) return;
    timer = setTimeout(function () {
      timer = null;
      syncAll(true);
      voTapped = false;
    }, 0);
  }

  // Before anything handles a tap, click, key press or submit (Add to Cart, express buttons, sticky bars), and again
  // once the page's own handlers have run (an app may have just written the size).
  ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown', 'submit'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      syncAll(true);
      // Only a click (not a scroll or Tab key that starts on VO's size row), and only for the deferred check.
      if (type === 'click' && event.target && event.target.closest && event.target.closest('#bcpo')) voTapped = true;
      syncLater();
    }, { capture: true, passive: true });
  });

  // Change and input: check once the event has reached every listener.
  ['change', 'input'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var target = event.target;
      if (target && target.name === 'id' && event.bubbles) announced.set(target, target.value);
      syncLater();
    }, true);
  });

  // The theme fires variant:changed before it updates the field, so remember the id and set it afterwards.
  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !event.target || !event.target.querySelectorAll) return;

    var forms = event.target.querySelectorAll(FORM_SELECTOR);
    for (var i = 0; i < forms.length; i++) stateOf(forms[i]).hint = String(variant.id);
    syncLater();
  });

  syncAll(false);
})();

/*
 * Cart drawer low-stock note ("Only 1 left" / "Only 2 left").
 *
 * The Section Store cart drawer (<section-store--cart-block>) draws its lines inside an open shadow root and redraws
 * them whenever the cart changes. For each line whose variant genuinely has 1 or 2 units left, a red note is shown
 * after the size text (or under the product title when the line has no size).
 *
 * The stock comes from sections/cart-stock-data.liquid through the Section Rendering API. That section only reports a
 * number for variants whose stock is tracked by Shopify and cannot be oversold, and never for Simple Bundles sets, so
 * the note is only ever shown when it is true. The section is fetched again only when the drawer's lines or their
 * quantities change. If the drawer is missing, its markup changes, or the fetch fails, no note is shown.
 */
(function () {
  'use strict';

  if (!window.customElements || typeof window.fetch !== 'function' || typeof MutationObserver !== 'function') return;

  var TAG = 'section-store--cart-block';
  var SECTION_ID = 'cart-stock-data';
  var STYLE_ID = 'r3d-stock-style';
  var NOTE = 'r3d-stock';
  var CSS =
    '.' + NOTE + '{margin-left:8px;color:#c62828;font-weight:600;white-space:nowrap}' +
    '.' + NOTE + '--block{display:block;margin-left:0;font-size:var(--line-subtitle-size, 12px);line-height:1.35}' +
    '.' + NOTE + '-sep{position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}';
  var OBSERVE = { childList: true, subtree: true, attributes: true, attributeFilter: ['data-line-key', 'data-line-quantity', 'value'] };
  var LINE_SELECTOR = '.cart-line-items .cart-line-item[data-line-key]';

  var drawers = [];
  var stock = emptyMap();
  var fetchedFor = null;
  var loading = false;
  var timer = null;

  function emptyMap() {
    return Object.create ? Object.create(null) : {};
  }

  function sectionUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root !== 'string' || !root) root = '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + '?sections=' + SECTION_ID;
  }

  // Units left for a line (1 or 2), or 0 when no note should be shown.
  function unitsLeft(key) {
    var value = key !== null && Object.prototype.hasOwnProperty.call(stock, key) ? stock[key] : null;
    return value === 1 || value === 2 ? value : 0;
  }

  // The lines and their quantities as drawn, e.g. "123:abc=1|456:def=2". Typing in a quantity box does not change it
  // (only the drawer redrawing the line with the new quantity does).
  function signature() {
    var parts = [];

    for (var d = 0; d < drawers.length; d++) {
      var lines = drawers[d].root.querySelectorAll(LINE_SELECTOR);

      for (var i = 0; i < lines.length; i++) {
        var input = lines[i].querySelector('[data-line-quantity-input]');
        var quantity = input ? input.getAttribute('data-line-quantity') || input.getAttribute('value') || '' : '';
        parts.push(lines[i].getAttribute('data-line-key') + '=' + quantity);
      }
    }

    return parts.join('|');
  }

  function ensureStyle(root) {
    var existing = root.getElementById ? root.getElementById(STYLE_ID) : root.querySelector('#' + STYLE_ID);
    if (existing) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    root.appendChild(style);
  }

  // Shows the right note on one line (or none), keeping a note that is already right so nothing is redrawn needlessly.
  // Free reward gifts never get a note (the shopper cannot buy more of them).
  function placeLine(line) {
    var left = line.classList.contains('cart-line-item--free-gift') ? 0 : unitsLeft(line.getAttribute('data-line-key'));
    var subtitle = line.querySelector('.cart-line-subtitle');
    // Inside the size line the note starts with a visually hidden ", " (non-breaking space) so screen readers and
    // copied text read "Medium, Only 1 left" rather than "MediumOnly 1 left".
    var text = left ? (subtitle ? ',\u00a0' : '') + 'Only ' + left + ' left' : '';
    var title = subtitle ? null : line.querySelector('.cart-line-title');
    var notes = line.querySelectorAll('.' + NOTE);
    var kept = null;
    var note;

    for (var i = 0; i < notes.length; i++) {
      note = notes[i];
      var fits = left && !kept && note.textContent === text && (subtitle
        ? note.tagName === 'SPAN' && note.parentNode === subtitle
        : note.tagName === 'DIV' && !!title && note.previousElementSibling === title);

      if (fits) {
        kept = note;
      } else if (note.parentNode) {
        note.parentNode.removeChild(note);
      }
    }

    if (!left || kept || !(subtitle || (title && title.parentNode))) return;

    if (subtitle) {
      note = document.createElement('span');
      note.className = NOTE;
      var sep = document.createElement('span');
      sep.className = NOTE + '-sep';
      sep.textContent = ',\u00a0';
      note.appendChild(sep);
      note.appendChild(document.createTextNode('Only ' + left + ' left'));
      subtitle.appendChild(note);
    } else {
      note = document.createElement('div');
      note.className = NOTE + ' ' + NOTE + '--block';
      note.textContent = text;
      title.parentNode.insertBefore(note, title.nextSibling);
    }
  }

  // Writes with the drawer's observer off, so the notes never trigger another update.
  function place(drawer) {
    var root = drawer.root;

    drawer.observer.disconnect();

    try {
      ensureStyle(root);

      var lines = root.querySelectorAll(LINE_SELECTOR);
      for (var i = 0; i < lines.length; i++) placeLine(lines[i]);

      // A note left outside any current line (the drawer moved or replaced its markup).
      var notes = root.querySelectorAll('.' + NOTE);
      for (var n = 0; n < notes.length; n++) {
        if (!notes[n].closest(LINE_SELECTOR) && notes[n].parentNode) notes[n].parentNode.removeChild(notes[n]);
      }
    } catch (error) {
      // Leave the drawer as the app drew it.
    }

    try {
      drawer.observer.observe(root, OBSERVE);
    } catch (error) {}
  }

  // Redraws the notes now from the stock already known (before the browser paints, so they do not blink when the
  // drawer redraws its lines), then checks shortly after whether the lines changed enough to need fresh stock.
  function update() {
    for (var i = 0; i < drawers.length; i++) place(drawers[i]);
    scheduleCheck();
  }

  function scheduleCheck() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(check, 150);
  }

  function check() {
    timer = null;

    var current;
    try {
      current = signature();
    } catch (error) {
      return;
    }

    // An empty basket forgets the stock, so lines added again later get fresh figures.
    if (!current) {
      fetchedFor = null;
      stock = emptyMap();
      return;
    }

    // The stock for exactly these lines is already loaded (or loading).
    if (current === fetchedFor || loading) return;
    load(current);
  }

  function parse(data) {
    var map = emptyMap();
    var html = data && data[SECTION_ID];
    if (typeof html !== 'string' || typeof DOMParser !== 'function') return map;

    var script = new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-cart-stock]');
    var values = script ? JSON.parse(script.textContent) : null;
    if (!values || typeof values !== 'object' || values instanceof Array) return map;

    for (var key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key) && (values[key] === 1 || values[key] === 2)) map[key] = values[key];
    }

    return map;
  }

  function load(forSignature) {
    var settled = false;
    var guard = null;
    loading = true;
    fetchedFor = forSignature;

    function done(map) {
      if (settled) return;
      settled = true;
      clearTimeout(guard);
      loading = false;
      stock = map;
      try {
        update();
      } catch (error) {}
    }

    // Any failure (network, HTTP error, section missing, bad JSON) gives no stock, so no note. A failed load is not
    // retried until the basket changes. A request still pending after 10s counts as failed (its late answer is ignored).
    guard = setTimeout(function () {
      done(emptyMap());
    }, 10000);

    try {
      window.fetch(sectionUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response && response.ok ? response.json() : null;
        })
        .then(parse)
        .then(null, emptyMap)
        .then(done);
    } catch (error) {
      done(emptyMap());
    }
  }

  // Only changes to the lines matter (not the drawer's timer, reward bar or upsells).
  function touchesLines(drawer, records) {
    var lines = drawer.root.querySelector('.cart-line-items');
    if (!lines) return false;

    for (var i = 0; i < records.length; i++) {
      var target = records[i].target;
      if (target === lines || (target.contains && target.contains(lines)) || lines.contains(target)) return true;
    }

    return false;
  }

  function attach(host) {
    var root = host.shadowRoot;
    if (!root || typeof root.querySelectorAll !== 'function') return;

    for (var i = 0; i < drawers.length; i++) {
      if (drawers[i].host === host) return;
    }

    var drawer = { host: host, root: root, observer: null };
    drawer.observer = new MutationObserver(function (records) {
      try {
        if (touchesLines(drawer, records)) update();
      } catch (error) {}
    });
    drawers.push(drawer);
    update();
  }

  function findDrawers() {
    try {
      var hosts = document.querySelectorAll(TAG);
      for (var i = 0; i < hosts.length; i++) attach(hosts[i]);
    } catch (error) {}
  }

  function start() {
    var tries = 0;

    (function look() {
      findDrawers();
      if (!drawers.length && ++tries < 20) setTimeout(look, 500);
    })();

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', findDrawers);
  }

  try {
    window.customElements.whenDefined(TAG).then(start, function () {});
  } catch (error) {}
})();
